import React, { useState } from 'react';
import { X, Save } from 'lucide-react';

const InstructorSignupForm = ({ onClose, onSave, onSubmit, prefilledEmail }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: prefilledEmail || '',
    phone: '',
    password: '',
    confirmPassword: '',
    businessName: '',
    address: '',
    postcode: '',
    licenceNumber: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /\S+@\S+\.\S+/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Business address is required';
    }
    if (!formData.postcode.trim()) {
      newErrors.postcode = 'Postcode is required';
    }
    if (!formData.licenceNumber.trim()) {
      newErrors.licenceNumber = 'Instructor licence number is required';
    } else if (formData.licenceNumber.length !== 18) {
      newErrors.licenceNumber = 'Instructor licence number must be exactly 18 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const { confirmPassword, ...dataToSave } = formData;
      const submitData = {
        ...dataToSave,
        name: `${formData.firstName} ${formData.lastName}`,
        registrationDate: new Date().toISOString().split('T')[0],
        status: 'pending',
        userType: 'instructor'  // Mark as instructor
      };
      
      // Use onSubmit if provided (for public signup), otherwise use onSave (for admin create)
      if (onSubmit) {
        onSubmit(submitData);
      } else if (onSave) {
        onSave(submitData);
      }
    }
  };

  return (
    <div className="create-user-form">
      <div className="form-header">
        <h2>Instructor Registration</h2>
        {onClose && (
          <button onClick={onClose} className="btn-close">
            <X size={24} />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="user-form">
        {/* Personal Information */}
        <div className="form-section">
          <h3 className="section-title">Personal Information</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name *</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                className={`form-input ${errors.firstName ? 'error' : ''}`}
              />
              {errors.firstName && <span className="error-text">{errors.firstName}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Last Name *</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                className={`form-input ${errors.lastName ? 'error' : ''}`}
              />
              {errors.lastName && <span className="error-text">{errors.lastName}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                disabled={!!prefilledEmail}
                className={`form-input ${errors.email ? 'error' : ''}`}
                style={prefilledEmail ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
              />
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Phone *</label>
              <input
                type="tel"
                value={`${formData.phone}`}
                onChange={(e) => handleChange('phone', e.target.value)}
                className={`form-input ${errors.phone ? 'error' : ''}`}
                placeholder="+44 7700 900000"
              />
              {errors.phone && <span className="error-text">{errors.phone}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Password *</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder="Minimum 8 characters"
              />
              {errors.password && <span className="error-text">{errors.password}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Confirm Password *</label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
              />
              {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
            </div>
          </div>
        </div>

        {/* Business Information */}
        <div className="form-section">
          <h3 className="section-title">Business Information</h3>
          
          <div className="form-group">
            <label className="form-label">Business Name (Optional)</label>
            <input
              type="text"
              value={formData.businessName}
              onChange={(e) => handleChange('businessName', e.target.value)}
              className="form-input"
              placeholder="Your Driving School Name"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Business Address *</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              className={`form-input ${errors.address ? 'error' : ''}`}
              placeholder="123 Main Street"
            />
            {errors.address && <span className="error-text">{errors.address}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Postcode *</label>
            <input
              type="text"
              value={formData.postcode}
              onChange={(e) => handleChange('postcode', e.target.value)}
              className={`form-input ${errors.postcode ? 'error' : ''}`}
              placeholder="SW1A 1AA"
            />
            {errors.postcode && <span className="error-text">{errors.postcode}</span>}
          </div>
        </div>

        {/* Instructor Licence */}
        <div className="form-section">
          <h3 className="section-title">Instructor Qualification</h3>
          
          <div className="form-group">
            <label className="form-label">ADI Licence Number *</label>
            <input
              type="text"
              value={formData.licenceNumber}
              onChange={(e) => handleChange('licenceNumber', e.target.value)}
              className={`form-input ${errors.licenceNumber ? 'error' : ''}`}
              placeholder="18 character ADI number"
              maxLength={18}
            />
            {errors.licenceNumber && <span className="error-text">{errors.licenceNumber}</span>}
            <span className="input-hint">Enter your Approved Driving Instructor (ADI) licence number</span>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          {onClose && (
            <button type="button" onClick={onClose} className="btn-cancel">
              Cancel
            </button>
          )}
          <button type="submit" className="btn-save-form">
            <Save size={18} />
            Submit Registration
          </button>
        </div>
      </form>
    </div>
  );
};

export default InstructorSignupForm;